import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import { createStyles, makeStyles } from '@material-ui/core';
import { ErrorMessage } from 'components/ErrorMessage';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { PageHeading } from 'components/PageHeading';
import { PickerI18n } from 'components/PickerI18n';
import {
  Control,
  IndicatorsContainer,
  Option,
  Placeholder,
} from 'components/PickerI18n/PickerComponents';
import { useDiscoverableTenants } from 'hooks/useDiscoverableTenants';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

interface TenantOption {
  label: string;
  value: string;
}

export const useStyles = makeStyles(() =>
  createStyles({
    picker: {
      width: '100%',
    },
  }),
);

export interface TenantPickerProps {
  onPickTenantId: (value: string) => void;
}

/**
 * Allows the user to choose a tenant to sign into.
 */
export const TenantPicker: React.FC<TenantPickerProps> = (props) => {
  const { onPickTenantId } = props;
  const classes = useStyles(props);
  const { t } = useTranslation();
  const { error, loading, tenants } = useDiscoverableTenants();
  const [inputValue, setInputValue] = React.useState('');
  const inputValueOnBlur = React.useRef('');

  const tenantTypeToFilterBy = 'illuminate';
  const otherTenantTypesToAllow = ['learn', null, ''];

  const tenantTypeParam = React.useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('tenantType');
  }, []);

  const filteredTenants = React.useMemo(() => {
    if (!tenants) return [];
    if (!tenantTypeParam) return tenants;
    if (tenantTypeParam.toLowerCase() !== tenantTypeToFilterBy.toLowerCase()) return tenants;

    // find all of the tenants that are parents of Illuminate tenants
    const illuminateTenantParentIds = new Set<string>();
    const illuminateTenants = tenants.filter(
      (tenant) => tenant.tenantType?.toLowerCase() === tenantTypeToFilterBy.toLowerCase(),
    );
    illuminateTenants.forEach((tenant) => {
      if (tenant.parentTenant) {
        illuminateTenantParentIds.add(tenant.parentTenant);
      }
    });

    const tenantsToAdd = tenants.filter((tenant) => {
      if (tenant.tenantType?.toLowerCase() !== tenantTypeParam.toLowerCase()) {
        // if the tenant doesn't have a parent and doesn't have a tenant type, show it
        if (!tenant.parentTenant && !tenant.tenantType) {
          return true;
        }

        // if the tenant has a parent, and the tenant type is one of the other types to allow,
        // and there's no illuminate tenant with the same parent, show it
        if (
          tenant.parentTenant &&
          otherTenantTypesToAllow.includes(tenant.tenantType?.toLowerCase() ?? null) &&
          !illuminateTenantParentIds.has(tenant.parentTenant)
        ) {
          return true;
        }
        return false;
      }
      return true; // Illuminate type tenant, include it
    });

    // return only unique tenants
    return Array.from(new Set(tenantsToAdd));
  }, [tenants, tenantTypeParam, tenantTypeToFilterBy, otherTenantTypesToAllow]);

  const pickerOptions = React.useMemo(
    () =>
      filteredTenants.map((tenant) => ({
        label: tenant.displayName || tenant.name,
        value: tenant.id,
      })),
    [filteredTenants],
  );

  // react-select clears the input on blur. We want to preserve the input value,
  // so we store it on blur to set after being cleared.

  React.useEffect(() => {
    if (!inputValue && inputValueOnBlur.current) {
      setInputValue(inputValueOnBlur.current);
      inputValueOnBlur.current = '';
    }
  }, [inputValue]);

  function handleOnBlur() {
    inputValueOnBlur.current = inputValue;
  }

  if (loading) {
    return <LoadingIndicator data-testid="loading-tenant-data" />;
  }

  if (error) {
    return (
      <ErrorMessage
        data-testid="error-loading-tenant-data"
        title={t('global.fetchError')}
        message={error.message}
      />
    );
  }

  if (tenants.length === 0) {
    return (
      <Typography paragraph data-testid="no-tenant-data">
        {t('tenantPicker.noTenantData')}
      </Typography>
    );
  }

  return (
    <>
      <PageHeading>{t('tenantPicker.findYourTenant')}</PageHeading>
      <PickerI18n
        className={classes.picker}
        components={{ Control, Placeholder, IndicatorsContainer, Option }}
        showOnlyFilteredResults
        highlightFilteredMatches={false}
        options={pickerOptions}
        onChange={(option) => onPickTenantId((option as TenantOption).value)}
        inputId="select-tenant"
        label={t('tenantPicker.selectTenant')}
        placeholder={t('tenantPicker.selectTenant')}
        noOptionsMessage={({ inputValue }) => t('tenantPicker.noTenantFound', { inputValue })}
        openMenuOnFocus={Boolean(inputValue)}
        openMenuOnClick={Boolean(inputValue)}
        onInputChange={setInputValue}
        inputValue={inputValue}
        onBlur={handleOnBlur}
        tabSelectsValue={false}
      />
    </>
  );
};

export default TenantPicker;
